import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuService, UacActionPayload } from '../menu.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-uac-actions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './uac-actions.component.html',
  styleUrl: './uac-actions.component.css',
})
export class UacActionsComponent implements OnInit {
  menus: any[] = [];
  /** Flattened list for dropdown: parents + children */
  allMenuNodes: Array<{ id: number; menu_name: string; depth: number }> = [];

  actions: any[] = [];
  editingAction: any = null;
  isCreate = false;
  selectedMainMenuId: number | null = null;


  // Holds navigation state when coming from MainMenusComponent
  private navigationState: any = null;

  constructor(
    private router: Router,
    private menuService: MenuService,
    private toasterService: ToasterService,
  ) {
    // Browser-only: read router state if present
    try {
      const nav = this.router.getCurrentNavigation?.();
      this.navigationState = nav?.extras?.state ?? null;
    } catch {
      this.navigationState = null;
    }
  }

  ngOnInit() {
    this.fetchMenus();

    // If navigated from MainMenusComponent with an editing action, open edit
    const editingAction = this.navigationState?.editingAction;
    if (editingAction?.id) {
      this.isCreate = false;
      this.editingAction = null;
      this.openEdit(editingAction);
    } else {
      this.openCreate();
    }
  }

  fetchMenus() {
    this.menuService.getMenus().subscribe({
      next: (res: any) => {
        this.menus = res?.body?.data || [];
        this.allMenuNodes = this.flattenMenuNodes(this.menus);

        // If nothing selected yet, default to first node
        if (!this.selectedMainMenuId && this.allMenuNodes.length) {
          this.selectedMainMenuId = this.allMenuNodes[0].id;
        }

        // Ensure create form reflects default selection
        if (this.isCreate && this.editingAction) {
          this.editingAction.main_menu_id = this.selectedMainMenuId;
        }
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to load menus',
        );
      },
    });
  }



  fetchActions() {
    // TODO: Implement API call to fetch UAC actions by selected menu.
    // Currently UI focuses on allowing selection of main menu + submenus in dropdown.
    const mainMenuId = this.editingAction?.main_menu_id ?? this.selectedMainMenuId;

    if (!mainMenuId) {
      this.actions = [];
      return;
    }

    // Placeholder: keep existing actions untouched until API is wired.
  }


  openCreate() {
    this.isCreate = true;
    this.editingAction = {
      main_menu_id: this.selectedMainMenuId,
      name: '',
      slug: '',
      action_url: '',
      is_active: true,
    };
  }

  openEdit(action: any) {
    this.isCreate = false;
    this.menuService.getUacActionById(action.id).subscribe({
      next: (res: any) => {
        const actionDetails = this.extractSingleResponseData(res) ?? action;
        this.editingAction = this.normalizeActionForEdit(actionDetails);
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to load action details',
        );
      },
    });
  }

  saveAction() {
    const isValid = this.validateForm();
    if (!isValid) return;

    const payload = this.buildActionPayload();
    const request$ = this.isCreate
      ? this.menuService.createUacAction(payload)
      : this.menuService.updateUacAction(this.editingAction.id, payload);

    request$.subscribe({
      next: (res: any) => {
        this.selectedMainMenuId = this.editingAction.main_menu_id;
        this.toasterService.setToaster(
          'success',
          'Success',
          res?.body?.message ?? `Action ${this.isCreate ? 'created' : 'updated'} successfully`,
        );
        this.fetchActions();
        this.openCreate();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? `Failed to ${this.isCreate ? 'create' : 'update'} action`,
        );
      },
    });
  }

  private validateForm(): boolean {
    const name = (this.editingAction?.name ?? '').toString().trim();
    const slug = (this.editingAction?.slug ?? '').toString().trim();
    const actionUrl = (this.editingAction?.action_url ?? '').toString().trim();

    if (!name) {
      this.toasterService.setToaster('error', 'Validation', 'Name is required.');
      return false;
    }
    if (name.length > 50) {
      this.toasterService.setToaster('error', 'Validation', 'Name must be at most 50 characters.');
      return false;
    }

    if (!slug) {
      this.toasterService.setToaster('error', 'Validation', 'Slug is required.');
      return false;
    }
    if (slug.length > 100) {
      this.toasterService.setToaster('error', 'Validation', 'Slug must be at most 100 characters.');
      return false;
    }

    if (!actionUrl) {
      this.toasterService.setToaster('error', 'Validation', 'Action URL is required.');
      return false;
    }
    if (actionUrl.length > 200) {
      this.toasterService.setToaster('error', 'Validation', 'Action URL must be at most 200 characters.');
      return false;
    }

    return true;
  }

  cancelEdit() {
    this.openCreate();
  }

  onMainMenuChange(mainMenuId: number | null) {
    this.selectedMainMenuId = mainMenuId;
    this.fetchActions();
  }

  deleteAction(action: any) {
    if (!confirm(`Delete ${action.name || action.action_name}?`)) {
      return;
    }

    this.menuService.deleteUacAction(action.id).subscribe({
      next: (res: any) => {
        this.toasterService.setToaster(
          'success',
          'Deleted',
          res?.body?.message ?? 'Action deleted successfully',
        );
        this.fetchActions();
      },
      error: (error: any) => {
        this.toasterService.setToaster(
          'error',
          'Error',
          error?.error?.message ?? 'Failed to delete action',
        );
      },
    });
  }

  backToMenus() {
    this.router.navigate(['/main-menus']);
  }

  private buildActionPayload(): UacActionPayload {
    return {
      main_menu_id: Number(this.editingAction.main_menu_id),
      name: this.editingAction.name,
      slug: this.editingAction.slug,
      action_url: this.editingAction.action_url,
      is_active: this.editingAction.is_active ? 1 : 0,
    };
  }

  private normalizeActionForEdit(action: any) {
    this.selectedMainMenuId = action.main_menu_id ?? action.menu_id ?? null;

    return {
      ...action,
      main_menu_id: this.selectedMainMenuId,
      name: action.name ?? action.action_name ?? '',
      slug: action.slug ?? '',
      action_url: action.action_url ?? action.url ?? '',
      is_active: action.is_active === 1 || action.is_active === true,
    };
  }

  private extractResponseData(res: any): any[] {
    const data = res?.body?.data ?? res?.body ?? [];
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data.actions)) {
      return data.actions;
    }
    if (Array.isArray(data.children)) {
      return data.children;
    }
    return [];
  }

  private extractSingleResponseData(res: any): any {
    const data = res?.body?.data ?? res?.body;
    return Array.isArray(data) ? data[0] : data;
  }

  private flattenMenuNodes(nodes: any[], depth = 0): Array<{ id: number; menu_name: string; depth: number }> {
    const out: Array<{ id: number; menu_name: string; depth: number }> = [];

    if (!Array.isArray(nodes)) return out;

    for (const node of nodes) {
      if (node?.id != null) {
        out.push({
          id: Number(node.id),
          menu_name: String(node.menu_name ?? node.slug ?? 'Menu'),
          depth,
        });
      }

      const children = node?.children;
      if (Array.isArray(children) && children.length) {
        out.push(...this.flattenMenuNodes(children, depth + 1));
      }
    }

    return out;
  }

}
